import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroCheckbox,
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLink,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { useApi, useAuth } from '../../shared/context';
import { QueryKeys, useShowAllDocumentsQuery } from '../../shared/hooks';
import { DateUtils, FileUtils, FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { GetDocumentTypeDescription } from '../../shared/utils/enum';
import { getApiResponseData } from '../../shared/utils/axios';

interface ShowAllDocumentsProps {
	dataRoomGuid: string | undefined;
	showDocuments: boolean;
	close: () => void;
}

export const ShowAllDocuments: React.FC<ShowAllDocumentsProps> = ({ dataRoomGuid, showDocuments, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const { currentUser } = useAuth();
	const allQuery = useShowAllDocumentsQuery(dataRoomGuid, {
		enabled: showDocuments,
		retry: false,
	});

	const downloadSelectedMutation = useMutation(
		async ({ values }: DonwloadSelectedValuesMutationVariables) => {
			const guids = Object.entries(values)
				.filter(item => item[1] == 'on')
				.map(item => item[0]);

			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.Documents.DocumentRequest(dataRoomGuid), guids)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (_data, { helpers }) => {
				snackbar.enqueueSnackbar('Successfully requested the selected documents for download.', {
					variant: 'success',
				});
				helpers.resetForm({
					values: {},
				});
				client.invalidateQueries(QueryKeys.DataRoom.DownloadRequests(dataRoomGuid, currentUser.userGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={showDocuments} onClose={() => close()} PaperProps={{ sx: { width: '700px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				All Documents
			</CerebroHeading>

			<CerebroText sx={{ marginBottom: 2 }}>
				Documents are listed in the order they were added to the dataroom, with the most recent at the top.
				Select multiple documents to download by using the checkboxes and click the Download button at the
				bottom of the page.
			</CerebroText>

			<Formik
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(downloadSelectedMutation.mutate)}
				initialValues={{}}
			>
				{({ isSubmitting, handleSubmit, values }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit}>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Select</TableCell>
										<TableCell>Name</TableCell>
										<TableCell>Type</TableCell>
										{!currentUser?.isBankUser && !currentUser?.isTeamMemberUser && (
											<TableCell>Date</TableCell>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{allQuery.isLoading && (
										<>
											<Skeleton height={60} />
											<Skeleton height={60} />
											<Skeleton height={60} />
										</>
									)}
									{allQuery.isSuccess && (
										<>
											{allQuery.data.map(doc => {
												return (
													<TableRow key={doc.guid}>
														<TableCell>
															<Field
																key={doc.guid}
																as={CerebroCheckbox}
																name={doc.guid}
																disabled={isSubmitting}
															/>
														</TableCell>
														<TableCell>
															<CerebroLink
																variant="link"
																target="_blank"
																rel="noreferrer"
																href={CerebroApiEndpoints.Documents.AbsoluteDownload(
																	dataRoomGuid,
																	doc.guid
																)}
															>
																{FileUtils.getFileTypeIcon(doc.fileName)} {doc.name}
															</CerebroLink>
														</TableCell>
														<TableCell>
															{GetDocumentTypeDescription(doc.documentType)}
														</TableCell>

														{!currentUser?.isBankUser && !currentUser?.isTeamMemberUser && (
															<TableCell>
																{DateUtils.FormatStringAsShortDate(
																	doc.documentDate.toString()
																)}
															</TableCell>
														)}
													</TableRow>
												);
											})}
											{allQuery.data.length == 0 && (
												<TableRow>
													<TableCell colSpan={3} style={{ textAlign: 'center' }}>
														There are no documents for this data room.
													</TableCell>
												</TableRow>
											)}
										</>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						<CerebroPrimaryButton
							type="submit"
							loading={isSubmitting}
							disabled={!Object.values(values).some(checked => checked)}
							sx={{ marginTop: 6 }}
						>
							Download
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type DonwloadSelectedValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<DownloadSelectedValues>;
interface DownloadSelectedValues {
	[key: string]: string;
}
